import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Truck, Package, Warehouse, ArrowRight, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";

const WAREHOUSE_IMG = "https://images.pexels.com/photos/27111449/pexels-photo-27111449.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const SHIPPING_IMG = "https://images.unsplash.com/photo-1713859326033-f75e04439c3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NjA1NTJ8MHwxfHNlYXJjaHwyfHxsb2dpc3RpY3MlMjB3b3JrZXIlMjB0YWJsZXQlMjBzaGlwcGluZyUyMHBvcnR8ZW58MHx8fHwxNzczNDMzMDE1fDA&ixlib=rb-4.1.0&q=85";
const WORKERS_IMG = "https://images.unsplash.com/photo-1759272548449-7b689a81c8fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxsb2dpc3RpY3MlMjB3b3JrZXIlMjB0YWJsZXQlMjBzaGlwcGluZyUyMHBvcnR8ZW58MHx8fHwxNzczNDMzMDE1fDA&ixlib=rb-4.1.0&q=85";

export default function ServicesPage() {
  const { t } = useTranslation();
  const services = [
    {
      id: "trasporti",
      icon: Truck,
      title: t("services_page.transport_title"),
      subtitle: t("services_page.transport_subtitle"),
      image: SHIPPING_IMG,
      description: t("services_page.transport_desc"),
      features: [
        t("services_page.transport_f1"), t("services_page.transport_f2"), t("services_page.transport_f3"),
        t("services_page.transport_f4"), t("services_page.transport_f5"), t("services_page.transport_f6"),
      ],
    },
    {
      id: "logistica",
      icon: Package,
      title: t("services_page.logistics_title"),
      subtitle: t("services_page.logistics_subtitle"),
      image: WORKERS_IMG,
      description: t("services_page.logistics_desc"),
      features: [
        t("services_page.logistics_f1"), t("services_page.logistics_f2"), t("services_page.logistics_f3"),
        t("services_page.logistics_f4"), t("services_page.logistics_f5"), t("services_page.logistics_f6"),
      ],
    },
    {
      id: "magazzino",
      icon: Warehouse,
      title: t("services_page.warehouse_title"),
      subtitle: t("services_page.warehouse_subtitle"),
      image: WAREHOUSE_IMG,
      description: t("services_page.warehouse_desc"),
      features: [
        t("services_page.warehouse_f1"), t("services_page.warehouse_f2"), t("services_page.warehouse_f3"),
        t("services_page.warehouse_f4"), t("services_page.warehouse_f5"), t("services_page.warehouse_f6"),
      ],
    },
  ];

  return (
    <main>
      {/* Hero */}
      <section className="bg-[#09090B] pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="text-[#FF5200] text-sm font-bold tracking-widest uppercase mb-4 font-heading">
              {t("services_page.hero_tagline")}
            </p>
            <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight uppercase leading-[0.9]">
              {t("services_page.hero_title_l1")}
              <br />
              <span className="text-[#FF5200]">{t("services_page.hero_title_l2")}</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Services Detail */}
      {services.map((service, idx) => (
        <section key={service.id} className={`py-20 md:py-28 ${idx % 2 === 0 ? "bg-white" : "bg-[#FAFAFA]"}`} data-testid={`service-detail-${service.id}`}>
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center ${idx % 2 !== 0 ? "lg:flex-row-reverse" : ""}`}>
              <motion.div initial={{ opacity: 0, x: idx % 2 === 0 ? -30 : 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5 }} className={idx % 2 !== 0 ? "lg:order-2" : ""}>
                <div className="flex items-center gap-3 mb-4">
                  <service.icon size={32} className="text-[#FF5200]" strokeWidth={1.5} />
                  <span className="text-sm font-bold tracking-widest uppercase text-zinc-500 font-heading">{service.subtitle}</span>
                </div>
                <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase tracking-tight text-[#09090B] mb-6">
                  {service.title}
                </h2>
                <p className="text-zinc-600 text-lg leading-relaxed mb-8 font-body">
                  {service.description}
                </p>
                <ul className="space-y-3 mb-8">
                  {service.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <CheckCircle2 size={20} className="text-[#FF5200] mt-0.5 flex-shrink-0" />
                      <span className="text-zinc-700 font-body">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/preventivo" data-testid={`service-cta-${service.id}`}
                  className="inline-flex items-center gap-2 bg-[#FF5200] text-white px-8 py-4 font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors duration-200 font-heading text-sm">
                  {t("services_page.cta_quote")} <ArrowRight size={18} />
                </Link>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: idx % 2 === 0 ? 30 : -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, delay: 0.1 }} className={idx % 2 !== 0 ? "lg:order-1" : ""}>
                <div className="relative overflow-hidden border-2 border-zinc-200">
                  <img src={service.image} alt={service.title} className="w-full h-80 md:h-[480px] object-cover" />
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#09090B]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight uppercase mb-6">
            {t("services_page.cta_title")}
          </h2>
          <p className="text-zinc-400 text-lg max-w-xl mx-auto mb-10 font-body">
            {t("services_page.cta_desc")}
          </p>
          <Link to="/preventivo" data-testid="services-bottom-cta"
            className="inline-flex items-center gap-2 bg-[#FF5200] text-white px-8 py-4 font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors duration-200 font-heading text-sm">
            {t("services_page.cta_btn")} <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </main>
  );
}
